import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Download, RefreshCw } from 'lucide-react';
import { CrifForm } from '@/components/CrifForm';

const REPORT_OPTIONS = [
  { value: 'verifica_cf', label: 'Verifica Codice Fiscale', color: 'text-emerald-600 bg-emerald-50', cat: 'persona', needsName: false, isCrif: false },
  { value: 'report_persona', label: 'Report Persona', color: 'text-blue-600 bg-blue-50', cat: 'persona', needsName: true, isCrif: false },
  { value: 'patrimoniale_persona', label: 'Report Patrimoniale', color: 'text-purple-600 bg-purple-50', cat: 'persona', needsName: true, isCrif: false },
  { value: 'negativita', label: 'Negatività e Protesti', color: 'text-red-600 bg-red-50', cat: 'persona', needsName: false, isCrif: false },
  { value: 'crif_persona', label: 'CRIF Persona', color: 'text-amber-600 bg-amber-50', cat: 'persona', needsName: false, isCrif: true },
  { value: 'eredi', label: 'Ricerca Eredi', color: 'text-indigo-600 bg-indigo-50', cat: 'persona', needsName: true, isCrif: false },
  { value: 'report_azienda', label: 'Report Azienda', color: 'text-teal-600 bg-teal-50', cat: 'azienda', needsName: false, isCrif: false },
  { value: 'crif_azienda', label: 'CRIF Azienda', color: 'text-orange-600 bg-orange-50', cat: 'azienda', needsName: false, isCrif: true },
];

export { REPORT_OPTIONS };

export const ReportSelector = ({ selectedReport, onSelect }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Seleziona tipo di report</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {REPORT_OPTIONS.map(opt => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`p-3 rounded-xl border-2 text-left transition-all ${
            selectedReport === opt.value
              ? 'border-indigo-500 bg-indigo-50 shadow-sm'
              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
          }`}
          data-testid={`risk-type-${opt.value}`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${opt.color}`}>
            <Search className="w-4 h-4" />
          </div>
          <p className="text-sm font-medium text-slate-900">{opt.label}</p>
          <p className="text-xs text-slate-400 mt-0.5">{opt.cat === 'azienda' ? 'Azienda' : 'Persona'}</p>
        </button>
      ))}
    </div>
  </div>
);

export const ReportForm = ({ selectedReport, onSubmit, onCrifSubmit, loading }) => {
  const [form, setForm] = useState({ codice_fiscale: '', partita_iva: '', nome: '', cognome: '', nome_azienda: '' });
  const currentReport = REPORT_OPTIONS.find(r => r.value === selectedReport);
  const isCompany = currentReport?.cat === 'azienda';

  const handleSubmit = () => {
    onSubmit({ report_type: selectedReport, ...form });
  };

  if (currentReport?.isCrif) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
          {currentReport.label} - Form Avanzato
        </h2>
        <CrifForm
          type={selectedReport === 'crif_azienda' ? 'azienda' : 'persona'}
          onSubmit={onCrifSubmit}
          loading={loading}
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
        {isCompany ? 'Dati Azienda' : 'Dati Persona'}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Codice Fiscale *</label>
          <Input
            value={form.codice_fiscale}
            onChange={e => setForm(f => ({...f, codice_fiscale: e.target.value.toUpperCase()}))}
            placeholder="RSSMRA80A01H501U"
            maxLength={16}
            className="uppercase"
            data-testid="risk-cf-input"
          />
        </div>
        {isCompany && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Partita IVA</label>
              <Input
                value={form.partita_iva}
                onChange={e => setForm(f => ({...f, partita_iva: e.target.value}))}
                placeholder="12345678901"
                maxLength={11}
                data-testid="risk-piva-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome Azienda *</label>
              <Input
                value={form.nome_azienda}
                onChange={e => setForm(f => ({...f, nome_azienda: e.target.value}))}
                placeholder="Mario Rossi S.r.l."
                data-testid="risk-company-name-input"
              />
            </div>
          </>
        )}
        {!isCompany && currentReport?.needsName && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome *</label>
              <Input value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} placeholder="Mario" data-testid="risk-nome-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cognome *</label>
              <Input value={form.cognome} onChange={e => setForm(f => ({...f, cognome: e.target.value}))} placeholder="Rossi" data-testid="risk-cognome-input" />
            </div>
          </>
        )}
      </div>
      <div className="mt-4">
        <Button
          onClick={handleSubmit}
          disabled={loading || (!form.codice_fiscale && !form.partita_iva)}
          className="bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white w-full md:w-auto"
          data-testid="risk-search-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
          Esegui {currentReport?.label}
        </Button>
      </div>
    </div>
  );
};

export const ReportResult = ({ result, onCheckStatus, onDownload, checkingStatus }) => {
  if (!result) return null;

  const renderData = (data, depth = 0) => {
    if (!data || typeof data !== 'object') return <span className="text-slate-700">{String(data)}</span>;
    if (Array.isArray(data)) {
      return (
        <div className="space-y-1 ml-4">
          {data.map((item, i) => <div key={i} className="border-l-2 border-slate-200 pl-3">{renderData(item, depth + 1)}</div>)}
        </div>
      );
    }
    return (
      <div className={`space-y-1 ${depth > 0 ? 'ml-4' : ''}`}>
        {Object.entries(data).map(([key, val]) => (
          <div key={key} className="flex flex-wrap gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase min-w-[140px]">{key.replace(/_/g, ' ')}:</span>
            {typeof val === 'object' && val !== null ? renderData(val, depth + 1) : (
              <span className={`text-sm ${
                val === true ? 'text-emerald-600 font-medium' :
                val === false ? 'text-red-600 font-medium' :
                'text-slate-800'
              }`}>{val === true ? 'Sì' : val === false ? 'No' : val === null ? '-' : String(val)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-xl border-2 p-6 ${result.success ? 'border-emerald-200' : 'border-red-200'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {result.success ? (
            <Badge className="bg-emerald-100 text-emerald-700">Successo</Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700">Errore</Badge>
          )}
          <span className="text-sm text-slate-500">{result.report_label}</span>
          {result.state && (
            <Badge variant="outline" className={result.state === 'PENDING' ? 'border-amber-400 text-amber-600 animate-pulse' : result.state === 'COMPLETE' ? 'border-emerald-400 text-emerald-600' : ''}>
              {result.state}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {result.state === 'PENDING' && result.api_request_id && (
            <Button size="sm" variant="outline" onClick={() => onCheckStatus(result.report_type, result.api_request_id)} disabled={checkingStatus} data-testid="risk-check-status-btn">
              {checkingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Controlla Stato
            </Button>
          )}
          {result.state === 'COMPLETE' && result.has_download && result.api_request_id && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onDownload(result.report_type, result.api_request_id)} data-testid="risk-download-btn">
              <Download className="w-4 h-4 mr-1" /> Scarica PDF
            </Button>
          )}
        </div>
      </div>
      {result.state === 'PENDING' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <p className="text-sm text-amber-700">Il report è in elaborazione. Clicca <strong>"Controlla Stato"</strong> per verificare se è pronto.</p>
        </div>
      )}
      {result.error && <p className="text-sm text-red-600 mb-3">{typeof result.error === 'string' ? result.error : JSON.stringify(result.error)}</p>}
      {result.data && (
        <div className="bg-slate-50 rounded-lg p-4 overflow-auto max-h-[500px]">
          {renderData(result.data)}
        </div>
      )}
    </div>
  );
};
